/**
 * 약관 버전 상세·등록·수정.
 *
 * - `/admin/terms/new?type=...` → 등록
 * - `/admin/terms/:termsId` → 보기, `?mode=edit`이면 수정
 *
 * 시행 예정·시행 중·지난 버전 모두 수정할 수 있다. 단 동의 기록은 버전 ID를 가리키므로
 * 시행된 버전의 본문을 고치면 이미 동의한 본문도 바뀐다 — 수정 화면에서 경고하고,
 * 실질 개정은 새 버전 등록을 권한다. 삭제는 시행 예정 버전만 화면에서 허용한다(운영 정책).
 * 보기 모드에서는 같은 종류의 버전 이력을 함께 보여준다.
 */
import type {
  TermsType,
  TermsUpdateRequest,
} from '@/entities/terms/api/terms.api';
import {
  useCreateTerms,
  useDeleteTerms,
  useTermsDetail,
  useTermsTypes,
  useUpdateTerms,
} from '@/entities/terms/hooks/useTerms';
import { useMenuAccess } from '@/layouts/MenuAccessContext';
import { TermsVersionHistorySection } from '@/pages/terms/TermsVersionHistorySection';
import {
  getNowDateTimeParts,
  isScheduled,
  isTermsContentEmpty,
  toServerDateTime,
} from '@/pages/terms/termsUtils';
import { DataCard } from '@/shared/components/DataCard';
import { DatePicker } from '@/shared/components/DatePicker';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { PageHeader } from '@/shared/components/PageHeader';
import { TimePicker } from '@/shared/components/TimePicker';
import { TinyMceEditor } from '@/shared/components/TinyMceEditor';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/shared/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import { FooterRow } from '@/shared/components/ui/row';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Separator } from '@/shared/components/ui/separator';
import {
  ROUTE_PATHS,
  termsDetailPath,
} from '@/shared/constants/routes';
import { formatDateTime } from '@/shared/lib/dateFormat';
import { splitDateTimeLocalValue } from '@/shared/lib/datePickerUtils';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import {
  Loader2,
  Save,
  ScrollText,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

interface TermsFormState {
  type: TermsType | '';
  title: string;
  effectiveDate: string;
  effectiveTime: string;
  changeSummary: string;
  content: string;
}

const createEmptyForm = (): TermsFormState => {
  const now = getNowDateTimeParts();
  return {
    type: '',
    title: '',
    effectiveDate: now.date,
    effectiveTime: now.time,
    changeSummary: '',
    content: '',
  };
};

/** 보기 모드 메타 항목 — 라벨 폭을 고정해 값이 세로로 정렬되게 한다 */
const InfoRow: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div className="flex gap-3 text-sm">
    <dt className="w-24 shrink-0 text-ruda-text-tertiary">{label}</dt>
    <dd className="flex-1 text-foreground">{children}</dd>
  </div>
);

export const TermsFormPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { termsId: termsIdParam } = useParams({ strict: false }) as {
    termsId?: string;
  };
  const search = useSearch({ strict: false }) as {
    mode?: string;
    type?: string;
  };
  const { readOnly: menuReadOnly } = useMenuAccess();

  const isNew = !termsIdParam;
  const termsId = isNew ? 0 : Number(termsIdParam);

  const { data: detail, isLoading } = useTermsDetail(termsId);
  const { data: types } = useTermsTypes();
  const createTerms = useCreateTerms();
  const updateTerms = useUpdateTerms();
  const deleteTerms = useDeleteTerms();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [form, setForm] = useState<TermsFormState>(createEmptyForm);
  /**
   * 에디터 remount 키.
   * TinyMCE는 사용자 입력 기준으로 내용을 들고 있어, 폼 state만 바꾸면 화면이 그대로 남을 수 있다.
   * 기존 버전 내용을 채울 때 이 값을 올려 에디터를 다시 마운트한다.
   */
  const [editorKey, setEditorKey] = useState(0);
  /** TinyMCE 초기화 완료 여부 — 초기화 전에 내용을 넣으면 화면에 반영되지 않는다 */
  const [isEditorReady, setIsEditorReady] = useState(false);
  /** 이미 채워 넣은 원본 버전 — 같은 버전을 두 번 덮어쓰지 않도록 기억한다 */
  const [prefilledSourceId, setPrefilledSourceId] = useState<number | null>(
    null,
  );

  /**
   * 등록 모드에서 내용을 가져올 원본 버전 — 선택한 종류의 현재 버전.
   * 등록 가능한 종류는 시행 예정 버전이 없으므로 현재 버전이 가장 마지막 버전이다.
   */
  const sourceVersion = isNew
    ? ((types ?? []).find((item) => item.type === form.type)?.current ?? null)
    : null;
  const { data: sourceDetail } = useTermsDetail(sourceVersion?.termsId ?? 0);

  const isEditMode = !isNew && search.mode === 'edit';
  const isFormMode = isNew || isEditMode;
  const isSaving = createTerms.isPending || updateTerms.isPending;
  /** 목록 API의 current — 지금 시각에 적용 중인 버전만 시행 태그를 붙인다 */
  const isInEffect = Boolean(
    detail &&
      (types ?? []).some(
        (item) => item.current?.termsId === detail.termsId,
      ),
  );

  /** 등록 가능한 종류 — 시행 예정 버전이 있으면 등록 대신 그 버전을 수정해야 한다 */
  const creatableTypes = useMemo(
    () => (types ?? []).filter((item) => !item.scheduled),
    [types],
  );

  // 등록 모드: 목록에서 넘어온 종류를 기본 선택
  useEffect(() => {
    if (!isNew) return;
    const requested = search.type;
    if (!requested) return;
    setForm((prev) =>
      prev.type ? prev : { ...prev, type: requested as TermsType },
    );
  }, [isNew, search.type]);

  // 상세 로드 후 폼 초기화
  useEffect(() => {
    if (!detail) return;
    const parts = splitDateTimeLocalValue(detail.effectiveAt);
    setForm({
      type: detail.type,
      title: detail.title,
      effectiveDate: parts.date,
      effectiveTime: parts.time || '00:00',
      changeSummary: detail.changeSummary ?? '',
      content: detail.content,
    });
  }, [detail]);

  // 읽기 전용 메뉴 권한으로 URL에 mode=edit을 붙여 들어온 경우 보기로 되돌린다
  useEffect(() => {
    if (!isEditMode || !detail || !menuReadOnly) return;
    toast.error('수정 권한이 없습니다.');
    navigate({ to: termsDetailPath(detail.termsId), replace: true } as never);
  }, [isEditMode, detail, menuReadOnly, navigate, toast]);

  /** 원본 버전을 가져오는 중인지 — 에디터 초기화와 상세 조회가 모두 끝날 때까지 */
  const isPrefilling =
    isNew &&
    sourceVersion !== null &&
    prefilledSourceId !== sourceVersion.termsId;

  /**
   * 기존 버전의 제목·본문을 자동으로 채운다.
   * 에디터 초기화 전에 넣으면 화면에 반영되지 않으므로 둘 다 준비된 뒤에 한 번만 적용하고,
   * 시행일시·변경 요약은 새로 입력해야 하므로 건드리지 않는다.
   * 종류를 바꾸면 그 종류의 현재 버전으로 다시 채운다.
   */
  useEffect(() => {
    if (!isNew || !sourceDetail || !isEditorReady) return;
    if (prefilledSourceId === sourceDetail.termsId) return;
    setPrefilledSourceId(sourceDetail.termsId);
    setForm((prev) => ({
      ...prev,
      title: sourceDetail.title,
      content: sourceDetail.content,
    }));
    setEditorKey((prev) => prev + 1);
  }, [isNew, sourceDetail, isEditorReady, prefilledSourceId]);

  const goList = () => {
    navigate({ to: ROUTE_PATHS.TERMS } as never);
  };

  /** 입력 검증 — 통과하면 공통 payload를 돌려준다 */
  const buildPayload = (): TermsUpdateRequest | null => {
    if (!form.title.trim()) {
      toast.error('제목을 입력해주세요.');
      return null;
    }
    if (!form.effectiveDate) {
      toast.error('시행일시를 입력해주세요.');
      return null;
    }
    if (isTermsContentEmpty(form.content)) {
      toast.error('본문을 입력해주세요.');
      return null;
    }
    const changeSummary = form.changeSummary.trim();
    return {
      title: form.title.trim(),
      content: form.content,
      effectiveAt: toServerDateTime(form.effectiveDate, form.effectiveTime),
      ...(changeSummary ? { changeSummary } : {}),
    };
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload) return;

    try {
      if (isNew) {
        if (!form.type) {
          toast.error('약관 종류를 선택해주세요.');
          return;
        }
        const result = await createTerms.mutateAsync({
          ...payload,
          type: form.type,
        });
        toast.success(`v${result.version} 버전을 등록했습니다.`);
        navigate({ to: termsDetailPath(result.termsId) } as never);
        return;
      }

      await updateTerms.mutateAsync({ termsId, data: payload });
      toast.success('약관 버전을 수정했습니다.');
      navigate({ to: termsDetailPath(termsId), replace: true } as never);
    } catch (e) {
      handleApiError(e);
    }
  };

  /**
   * 버전 삭제 — 시행 예정 버전만 지울 수 있다.
   * 서버는 동의가 없는 시행 버전 삭제도 허용하지만, 운영 정책상 이미 시행된 버전은
   * 이력으로 남기고 화면에서 막는다. 시행 예정이어도 진행 중인 SNS 가입이 그 버전을
   * 가리키면 서버가 409(TERMS_IN_USE)로 막으며, 그 사유 문구를 토스트로 보여준다.
   */
  const handleDelete = async () => {
    if (detail?.effective) {
      toast.error('이미 시행된 버전은 삭제할 수 없습니다.');
      setIsDeleteDialogOpen(false);
      return;
    }
    try {
      await deleteTerms.mutateAsync(termsId);
      toast.success(`v${detail?.version ?? ''} 버전을 삭제했습니다.`);
      setIsDeleteDialogOpen(false);
      goList();
    } catch (e) {
      setIsDeleteDialogOpen(false);
      handleApiError(e);
    }
  };

  if (!isNew && isLoading) {
    return <LoadingSpinner />;
  }

  if (!isNew && !detail) {
    return (
      <div className="space-y-5">
        <PageHeader
          icon={ScrollText}
          title="약관 상세"
          actions={
            <Button variant="outline" onClick={goList}>
              목록
            </Button>
          }
        />
      </div>
    );
  }

  const typeName = isNew
    ? (creatableTypes.find((item) => item.type === form.type)?.typeName ??
      '새 버전')
    : detail!.typeName;

  return (
    <div className="space-y-5">
      <PageHeader
        icon={ScrollText}
        title={isNew ? '약관 버전 등록' : `${typeName} v${detail!.version}`}
        description={
          isNew
            ? sourceVersion
              ? `현재 v${sourceVersion.version} 내용을 불러와 채웁니다. 수정해서 새 버전으로 등록하세요.`
              : '약관 종류를 선택해 새 버전을 등록합니다. 시행일시가 지나면 현재 버전이 됩니다.'
            : '약관 버전 상세와 버전 이력'
        }
        actions={
          isFormMode ? null : (
            <>
              {!menuReadOnly ? (
                <>
                  {!detail!.effective ? (
                    <Button
                      variant="outline"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setIsDeleteDialogOpen(true)}
                      disabled={deleteTerms.isPending}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      삭제
                    </Button>
                  ) : null}
                  <Button
                    className="ruda-button"
                    onClick={() =>
                      navigate({
                        to: termsDetailPath(detail!.termsId),
                        search: { mode: 'edit' },
                      } as never)
                    }
                  >
                    수정
                  </Button>
                </>
              ) : null}
              <Button variant="outline" onClick={goList}>
                목록
              </Button>
            </>
          )
        }
      />

      {isFormMode ? (
        <Card>
          <CardContent className="space-y-6">
            {isEditMode && detail!.effective ? (
              <Alert variant="warning">
                <TriangleAlert className="h-4 w-4" />
                <AlertTitle>이미 시행된 버전입니다</AlertTitle>
                <AlertDescription>
                  동의 기록은 이 버전을 가리키므로, 본문을 고치면 이미 동의한
                  사용자의 동의 본문도 함께 바뀝니다(수정 전 본문은 남지
                  않습니다). 오탈자 수정이 아닌 실질적인 개정은 새 버전으로
                  등록해 주세요.
                </AlertDescription>
              </Alert>
            ) : null}

            <div className="grid gap-4 md:grid-cols-12">
              <Field label="약관 종류" required className="md:col-span-4">
                {isNew ? (
                  <Select
                    value={form.type}
                    onValueChange={(value) =>
                      setForm((prev) => ({
                        ...prev,
                        type: value as TermsType,
                      }))
                    }
                  >
                    <SelectTrigger aria-label="약관 종류">
                      <SelectValue placeholder="약관 종류 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      {creatableTypes.map((item) => (
                        <SelectItem key={item.type} value={item.type}>
                          {item.typeName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={detail!.typeName} readOnly />
                )}
              </Field>

              <Field label="제목" required className="md:col-span-8">
                <Input
                  disabled={isPrefilling}
                  value={form.title}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, title: e.target.value }))
                  }
                  placeholder="약관 제목"
                />
              </Field>

              <Field
                label="시행일시"
                required
                className="md:col-span-6"
                hint={
                  isNew
                    ? '과거 일시도 등록할 수 있습니다. 과거면 등록 즉시 현재 버전이 됩니다.'
                    : '이전 버전 시행일시보다 늦고 다음 버전 시행일시보다 빨라야 합니다. 이미 시행된 버전은 미래로 옮길 수 없습니다.'
                }
              >
                <FooterRow gap={8} justify="start" wrap>
                  <DatePicker
                    value={form.effectiveDate}
                    onChange={(value) =>
                      setForm((prev) => ({ ...prev, effectiveDate: value }))
                    }
                    placeholder="시행일"
                    className="w-[150px]"
                  />
                  <TimePicker
                    value={form.effectiveTime || '00:00'}
                    onChange={(value) =>
                      setForm((prev) => ({ ...prev, effectiveTime: value }))
                    }
                    placeholder="시행 시간"
                    className="w-[120px]"
                  />
                </FooterRow>
              </Field>

              <Field label="변경 요약" className="md:col-span-6">
                <Input
                  value={form.changeSummary}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      changeSummary: e.target.value,
                    }))
                  }
                  placeholder="예) 제5조 개정"
                />
              </Field>
            </div>

            <Field label="본문" required>
              <div className="relative">
                <TinyMceEditor
                  key={editorKey}
                  value={form.content}
                  onChange={(html) =>
                    setForm((prev) => ({ ...prev, content: html }))
                  }
                  onReady={() => setIsEditorReady(true)}
                />
                {isPrefilling ? (
                  <div className="absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-12 bg-background/80 text-sm text-ruda-text-secondary">
                    <Loader2 className="h-4 w-4 animate-spin" />v
                    {sourceVersion!.version} 내용을 불러오는 중입니다...
                  </div>
                ) : null}
              </div>
            </Field>

            <Separator className="bg-ruda-border-light" />
            <FooterRow gap={8} wrap>
              <Button
                type="button"
                variant="outline"
                className="ruda-button-outline"
                onClick={() =>
                  isNew
                    ? goList()
                    : navigate({
                        to: termsDetailPath(termsId),
                        replace: true,
                      } as never)
                }
                disabled={isSaving}
              >
                취소
              </Button>
              <Button
                type="button"
                className="ruda-button"
                onClick={handleSave}
                disabled={isSaving || isPrefilling}
              >
                <Save className="mr-2 h-4 w-4" />
                {isNew ? '등록' : '수정'}
              </Button>
            </FooterRow>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* 상단 요약 영역 — 버전 정보·이력을 500px 높이로 고정하고 넘치면 각 카드에서 스크롤한다 */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className="flex h-[500px] flex-col gap-2">
              <h2 className="text-sm font-bold text-foreground">버전 정보</h2>
              <DataCard className="flex-1 overflow-y-auto">
                <div className="space-y-4 p-6">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-base font-bold text-foreground">
                      {detail!.title}
                    </span>
                    <Badge variant="secondary">v{detail!.version}</Badge>
                    {isScheduled(detail!.effectiveAt) ? (
                      <Badge variant="warning">시행 예정</Badge>
                    ) : isInEffect ? (
                      <Badge variant="success">시행</Badge>
                    ) : null}
                  </div>
                  <Separator className="bg-ruda-border-light" />
                  <dl className="grid gap-x-8 gap-y-3">
                    <InfoRow label="약관 종류">{detail!.typeName}</InfoRow>
                    <InfoRow label="시행일시">
                      {formatDateTime(detail!.effectiveAt)}
                    </InfoRow>
                    <InfoRow label="변경 요약">
                      {detail!.changeSummary || '-'}
                    </InfoRow>
                    <InfoRow label="등록자">
                      {detail!.createdByName || '-'}
                    </InfoRow>
                    <InfoRow label="등록일시">
                      {formatDateTime(detail!.createdAt)}
                    </InfoRow>
                    <InfoRow label="수정일시">
                      {formatDateTime(detail!.updatedAt)}
                    </InfoRow>
                  </dl>
                </div>
              </DataCard>
            </div>

            <TermsVersionHistorySection
              type={detail!.type}
              currentTermsId={detail!.termsId}
              className="h-[500px]"
            />
          </div>

          <div className="space-y-2">
            <h2 className="text-sm font-bold text-foreground">본문</h2>
            <DataCard>
              {/* 서버가 sanitize(script·style·iframe·on* 제거)한 HTML을 그대로 표시한다 */}
              <div
                className="ruda-rich-text p-6"
                dangerouslySetInnerHTML={{
                  __html: detail!.content || '<p>-</p>',
                }}
              />
            </DataCard>
          </div>
        </>
      )}

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          if (!deleteTerms.isPending) setIsDeleteDialogOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {typeName} v{detail?.version} 삭제
            </AlertDialogTitle>
            <AlertDialogDescription>
              시행 예정 버전을 삭제합니다. 삭제한 버전은 되돌릴 수 없으며,
              진행 중인 가입이 이 버전을 가리키고 있으면 삭제할 수 없습니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteTerms.isPending}>
              취소
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteTerms.isPending}
              onClick={(e) => {
                // 기본 동작(즉시 닫힘)을 막고 요청이 끝난 뒤 닫는다
                e.preventDefault();
                void handleDelete();
              }}
            >
              {deleteTerms.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              삭제
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
